<script lang="ts">
import { computed, defineComponent, ref, unref } from 'vue';
import { useCurrentImage } from '../composables/useCurrentImage';
import VolumeProperties from './VolumeProperties.vue';
import VolumeRendering from './VolumeRendering.vue';
import VolumePresets from './VolumePresets.vue';
import LayerList from './LayerList.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  components: { VolumeRendering, VolumePresets, VolumeProperties, LayerList },
  setup() {
    const { t } = useI18n();

    const { currentImageData, currentImage } = useCurrentImage();
    const hasCurrentImage = computed(() => !!currentImageData.value);
    const isImageLoading = computed(() => !!unref(currentImage.value?.loading));

    const { currentLayers } = useCurrentImage();
    const hasLayers = computed(() => !!currentLayers.value.length);

    const panels = ref<string[]>(['properties', 'layers']);

    return {
      t,
      panels,
      hasCurrentImage,
      hasLayers,
      isImageLoading,
    };
  },
});
</script>

<template>
  <div class="overflow-y-auto mx-2 mt-1 fill-height">
    <template v-if="hasCurrentImage">
      <v-skeleton-loader v-if="isImageLoading" type="image">
      </v-skeleton-loader>
      <volume-rendering v-else />
      <v-expansion-panels v-model="panels" multiple variant="accordion">
        <v-expansion-panel value="preset">
          <v-expansion-panel-title>
            <v-icon class="flex-grow-0 mr-4">mdi-palette</v-icon>
            {{ t('rendering.colorPresets') }}
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <volume-presets />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel value="properties">
          <v-expansion-panel-title>
            <v-icon class="flex-grow-0 mr-4">mdi-cube-scan</v-icon>
            {{ t('rendering.cinematicRendering') }}
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <volume-properties />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel v-if="hasLayers" value="layers">
          <v-expansion-panel-title>
            <v-icon class="flex-grow-0 mr-4">mdi-layers</v-icon>
            {{ t('rendering.layers') }}
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <layer-list />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
    <template v-else>
      <div class="empty-state ma-4 text-center">{{ t('rendering.noImageSelected') }}</div>
    </template>
  </div>
</template>